import { ImprovementGoal, Survey } from "@/types";
import { useDateFormatter, useTranslations } from "@/contexts/Translation";
import { InvisibleLink } from "../Link/Link";
import { goalProgress } from "./insights";
import { categoryLabel } from "./labels";
import styles from "./Index.module.css";

const PHASES = ["planning", "execution", "conclusion"] as const;

// Ongoing goals first, then the two latest finished.
export const dashboardGoals = (goals: ImprovementGoal[], now = new Date()) => {
  const withPhase = goals.map((goal) => ({ goal, ...goalProgress(goal, now) }));
  return [
    ...withPhase.filter((g) => g.phase !== "finished"),
    ...withPhase
      .filter((g) => g.phase === "finished")
      .sort((a, b) => b.goal.endDate.localeCompare(a.goal.endDate))
      .slice(0, 2),
  ];
};

const JourneyLine = ({ progress, phase }: { progress: number; phase: string }) => {
  const t = useTranslations("dashboard");
  const x = (i: number) => 8 + i * 92;
  return (
    <div className={styles.journeyLine}>
      <svg aria-hidden='true' viewBox='0 0 200 16' preserveAspectRatio='none'>
        <line x1={x(0)} x2={x(2)} y1='8' y2='8' stroke='var(--color-muted)' strokeWidth='4' strokeLinecap='round' />
        <line x1={x(0)} x2={x(0) + (x(2) - x(0)) * progress} y1='8' y2='8' stroke='var(--color-cta)' strokeWidth='4' strokeLinecap='round' />
        {PHASES.map((p, i) => (
          <circle key={p} cx={x(i)} cy='8' r='5' fill={progress >= i / 2 ? "var(--color-cta)" : "var(--color-muted)"} />
        ))}
      </svg>
      <ol className={styles.phases}>
        {PHASES.map((p) => (
          <li key={p} className={p === phase ? styles.phaseActive : undefined} aria-current={p === phase ? "step" : undefined}>
            {t(`phase_${p}`)}
          </li>
        ))}
      </ol>
    </div>
  );
};

const GoalList = ({ goals, surveys }: { goals: ImprovementGoal[]; surveys: Survey[] }) => {
  const t = useTranslations("dashboard");
  const tg = useTranslations("improvementGoals");
  const d = useDateFormatter();
  const surveyName = (id: string) => surveys.find((s) => s.id === id)?.surveyName;
  const list = dashboardGoals(goals);

  return (
    <section aria-labelledby='goals-heading'>
      <div className={styles.listHeading}>
        <h2 id='goals-heading' className={styles.panelTitle}>
          {tg("goalsTitle")}
        </h2>
      </div>
      {list.length === 0 && <p className={styles.rowMeta}>{tg("noGoals")}</p>}
      <div className={styles.rows}>
        {list.map(({ goal, progress, phase }) => {
          const isFinished = phase === "finished";
          const origin = surveyName(goal.originalSurvey);
          return (
            <InvisibleLink key={goal.id} className={isFinished ? styles.goalFinished : styles.goal} href={`/improvement/${goal.id}`}>
              <span className={styles.tag}>
                {isFinished ? t(goal.isSuccess === "yes" ? "goalSuccess" : goal.isSuccess === "no" ? "goalFailure" : "phase_finished") : t(`phase_${phase}`)}
              </span>
              <h3 className={styles.goalTitle}>{goal.name}</h3>
              <p className={styles.rowMeta}>
                {goal.categories?.length > 0 && <span>{goal.categories.map((c) => tg(categoryLabel(c).split(".")[1])).join(", ")}</span>}
                {origin && <span>{t("goalFromSurvey", origin)}</span>}
                <span>{tg("endDateLabel", d(goal.endDate))}</span>
              </p>
              {!isFinished && <JourneyLine progress={progress} phase={phase} />}
            </InvisibleLink>
          );
        })}
      </div>
    </section>
  );
};

export default GoalList;
